import useSWR from "swr";
import { create } from "zustand";

import { getSWRKey, SwrStore, useInterval } from "~utils";

import { AnalyticsEventType, useAnalyticsEvent } from "../analytics-event";
import { usePreferences } from "../preferences";
import { usePromptLimit } from "../prompt-limit";
import { isOldUser, useUser } from "../user";
import { useLastPromptPreferencesPersistence } from "./use-last-prompt-preferences-persistence";

export function usePromptPreferences(): usePromptPreferencesReturn {
  const { shouldPromptDate } = useStore();
  const { submitEvent } = useAnalyticsEvent();

  const acceptPrompt = (): void => {
    submitEvent({ type: AnalyticsEventType.AcceptPromptPreferences });
  };

  return { shouldPromptDate, acceptPrompt };
}

export type usePromptPreferencesReturn = {
  shouldPromptDate?: Date;
  acceptPrompt: () => void;
};

export function usePromptPreferencesEffect(): void {
  const { hasPersistedPreferences } = usePreferences();
  const { lastPrompted, setLastPrompted } = useLastPreferencesPrompted();
  const { canPrompt, reportPrompted } = usePromptLimit();
  const { submitEvent } = useAnalyticsEvent();
  const { shouldPromptDate, setShouldPromptDate } = useStore();
  const { user } = useUser();

  useInterval((): void => {
    if (hasPersistedPreferences) return;
    if (!isOldUser(user)) return;
    if (hasRecentlyPrompted(lastPrompted)) return;
    if (!canPrompt) return;
    // The persisted read only catches up a render or two after it is written, and the
    // interval would fire again in between. Hold the same window locally to cover that.
    if (shouldPromptDate !== undefined && hasRecentlyPrompted(shouldPromptDate))
      return;

    const now = new Date();
    setShouldPromptDate(now);
    setLastPrompted(now);
    reportPrompted(now);

    submitEvent({
      type: AnalyticsEventType.PromptPreferences,
    });
  });
}

function useLastPreferencesPrompted(): useLastPreferencesPromptedReturn {
  const {
    getLastPrompted: getLastPromptedPersisted,
    setLastPrompted: setLastPromptedPersisted,
  } = useLastPromptPreferencesPersistence();

  const { data, mutate } = useSWR(
    getSWRKey(SwrStore.LastPreferencesPrompted),
    getLastPromptedPersisted,
    { suspense: true },
  );

  const setLastPrompted = (date: Date): void =>
    void mutate(() => setLastPromptedPersisted(date).then(() => date), {
      optimisticData: date,
      revalidate: false,
    });

  return { lastPrompted: data ?? new Date(0), setLastPrompted };
}

type useLastPreferencesPromptedReturn = {
  lastPrompted: Date;
  setLastPrompted: (date: Date) => void;
};

function hasRecentlyPrompted(lastPrompted: Date): boolean {
  const targetDate = new Date(lastPrompted.getTime() + threeDaysMs);
  return new Date() < targetDate;
}

const threeDaysMs = 1000 * 60 * 60 * 24 * 3;

const useStore = create<StoreState>((set) => ({
  setShouldPromptDate: (shouldPromptDate: Date) => set({ shouldPromptDate }),
}));

type StoreState = {
  shouldPromptDate?: Date;
  setShouldPromptDate: (date: Date) => void;
};
